import {ApiClient} from '@hconnect/apiclient'
import {BaseDatatableData} from '@hconnect/common/maintain-datatable'
import {OrderByApiProperty, ApiFilter} from '@hconnect/common/maintain-filters'
import {AnalysisListType} from '@hconnect/common/types'
import {useQuery, UseQueryOptions} from '@tanstack/react-query'
import {AxiosError} from 'axios'

import {AnalysisListInfo} from '../types/analysis.types'

export type PagePermissions = {
  canCreateAnalysis: boolean
}

export interface ResponseType extends BaseDatatableData<AnalysisListInfo> {
  currentPage: number
  data: Array<AnalysisListInfo>
  totalCount: number
  permissions: PagePermissions
}

export const QueryKey = 'analysis-list'

type UseGetAnalysisListParams = {
  plantUpmId: string
  page: number
  rowsNumber: number
  listType: AnalysisListType
  filters?: ApiFilter[]
  order?: OrderByApiProperty[]
  options?: UseQueryOptions<ResponseType, AxiosError>
}

export const useGetAnalysisList = ({
  plantUpmId,
  page,
  rowsNumber,
  listType,
  filters,
  order,
  options
}: UseGetAnalysisListParams) => {
  const apiFilters = filters ? [...filters] : []

  const stoppageTimeIndex = apiFilters?.findIndex((f) => f.property === 'stoppageTime')
  if (stoppageTimeIndex !== undefined && stoppageTimeIndex >= 0) {
    const stoppageTimeFilter = apiFilters[stoppageTimeIndex]
    apiFilters?.splice(stoppageTimeIndex, 1)
    apiFilters?.push({...stoppageTimeFilter, property: 'stoppageStart'})
    apiFilters?.push({...stoppageTimeFilter, property: 'stoppageEnd'})
  }

  return useQuery<ResponseType, AxiosError>({
    queryKey: [QueryKey, page, rowsNumber, listType, apiFilters, order, plantUpmId],
    queryFn: async () => {
      const path = `/rootcausefailureanalysis/analyses/${listType}`
      return (
        await ApiClient.axiosInstance.post<ResponseType>(
          path,
          {
            pageSize: rowsNumber,
            pageNumber: page,
            filters: apiFilters,
            plantUpmId,
            orderBy: order
          },
          {}
        )
      ).data
    },

    // these default values will get moved to the api provider soon
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,

    // allow the callee to override the default options
    ...options
  })
}
